import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, BookOpen, ArrowRight, RotateCcw } from "lucide-react";
import TopNav from "../components/landing/TopNav";
import Footer from "../components/landing/Footer";
import { fetchArticles } from "../lib/soro";

const REVEAL = {
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
    transition: { duration: 0.7, ease: [0.2, 0.8, 0.2, 1] },
};

export default function Blog() {
    const reduce = useReducedMotion();
    const { pathname } = useLocation();

    const [articles, setArticles] = useState(null); // null = loading, [] = empty, [..] = ok
    const [error, setError] = useState(null);

    useEffect(() => {
        document.title = "Notes from BetweenUs — Blog";
        window.scrollTo(0, 0);
    }, [pathname]);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const list = await fetchArticles();
                if (cancelled) return;
                setArticles(Array.isArray(list) ? list : []);
            } catch (err) {
                if (cancelled) return;
                setError(err?.message || "Failed to load articles.");
                setArticles([]);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    const anim = reduce ? {} : REVEAL;

    return (
        <div
            className="relative min-h-screen bg-paper text-ink flex flex-col"
            data-testid="blog-page"
        >
            <TopNav variant="legal" />

            <main className="flex-1">
                {/* Hero */}
                <section
                    data-testid="blog-hero"
                    className="relative overflow-hidden border-b border-brand"
                >
                    <div className="absolute inset-0 hero-wash pointer-events-none" />
                    <div className="noise-overlay" />
                    <div className="relative mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-12 sm:pb-16">
                        <Link
                            to="/"
                            data-testid="blog-back-home"
                            className="inline-flex items-center gap-2 text-[12.5px] font-medium tracking-[-0.01em] text-[var(--brand-muted-2)] hover:text-[var(--brand-ink)] transition-colors"
                        >
                            <ArrowLeft size={14} strokeWidth={2} />
                            Back to home
                        </Link>

                        <motion.div
                            {...anim}
                            className="mt-6 inline-flex items-center gap-2 rounded-full border border-brand bg-white/80 backdrop-blur px-3 py-1 text-[11px] font-semibold tracking-[0.16em] uppercase text-[var(--brand-teal-2)]"
                            data-testid="blog-eyebrow"
                        >
                            <BookOpen size={12} strokeWidth={2.2} />
                            Notes &amp; field guides
                        </motion.div>

                        <motion.h1
                            {...anim}
                            transition={{
                                ...(anim.transition || {}),
                                delay: 0.05,
                            }}
                            className="mt-4 font-serif text-[36px] sm:text-[48px] leading-[1.05] tracking-[-0.02em] text-[var(--brand-ink)]"
                            data-testid="blog-title"
                        >
                            Notes from{" "}
                            <span className="italic text-[var(--brand-navy)]">
                                BetweenUs
                            </span>
                            .
                        </motion.h1>
                        <motion.p
                            {...anim}
                            transition={{
                                ...(anim.transition || {}),
                                delay: 0.1,
                            }}
                            className="mt-4 text-[16px] leading-[1.65] text-[var(--brand-muted-2)] max-w-2xl"
                            data-testid="blog-subtitle"
                        >
                            Field notes on shared spaces, hard conversations,
                            and the small habits that keep two people on the
                            same team. Written in the same calm voice you’ll
                            find in the app.
                        </motion.p>
                    </div>
                </section>

                {/* Article list */}
                <section className="relative">
                    <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                        {/* Loading */}
                        {articles === null && !error && (
                            <ul
                                data-testid="blog-list-loading"
                                className="grid gap-5 sm:gap-6"
                            >
                                {[0, 1, 2].map((i) => (
                                    <li
                                        key={i}
                                        className="rounded-[18px] bg-white border border-brand p-5 sm:p-6 shadow-[var(--shadow-soft)]"
                                        aria-hidden="true"
                                    >
                                        <div className="h-3 w-24 rounded-full bg-[rgba(14,27,42,0.06)]" />
                                        <div className="mt-4 h-6 w-3/4 rounded-md bg-[rgba(14,27,42,0.08)]" />
                                        <div className="mt-3 h-4 w-full rounded-md bg-[rgba(14,27,42,0.05)]" />
                                        <div className="mt-2 h-4 w-5/6 rounded-md bg-[rgba(14,27,42,0.05)]" />
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* Error */}
                        {error && (
                            <div
                                data-testid="blog-list-error"
                                className="rounded-[18px] bg-white border border-brand p-6 sm:p-8 shadow-[var(--shadow-soft)]"
                            >
                                <h2 className="font-serif text-[22px] tracking-[-0.01em] text-[var(--brand-ink)]">
                                    We couldn’t load the blog right now.
                                </h2>
                                <p className="mt-2 text-[14.5px] leading-[1.6] text-[var(--brand-muted-2)]">
                                    Please check your connection and try
                                    again, or come back in a few minutes.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => window.location.reload()}
                                    data-testid="blog-list-retry"
                                    className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[var(--brand-navy)] text-[var(--brand-paper)] text-[13.5px] font-semibold tracking-[-0.01em] hover:bg-[var(--brand-navy-2)] transition-colors shadow-[0_10px_24px_rgba(14,27,42,0.14)] ring-focus"
                                >
                                    <RotateCcw size={14} strokeWidth={2} />
                                    Try again
                                </button>
                            </div>
                        )}

                        {/* Empty */}
                        {articles !== null &&
                            !error &&
                            articles.length === 0 && (
                                <div
                                    data-testid="blog-list-empty"
                                    className="rounded-[18px] bg-white border border-brand p-8 sm:p-10 text-center shadow-[var(--shadow-soft)]"
                                >
                                    <span
                                        aria-hidden="true"
                                        className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[rgba(48,140,138,0.10)] text-[var(--brand-teal-2)]"
                                    >
                                        <BookOpen
                                            size={20}
                                            strokeWidth={1.8}
                                        />
                                    </span>
                                    <h2 className="mt-4 font-serif text-[22px] tracking-[-0.01em] text-[var(--brand-ink)]">
                                        New notes are on the way.
                                    </h2>
                                    <p className="mt-2 text-[14.5px] leading-[1.6] text-[var(--brand-muted-2)] max-w-md mx-auto">
                                        We’re writing the first set of field
                                        guides on shared spaces, repair
                                        conversations, and steadier home
                                        life. Check back soon.
                                    </p>
                                </div>
                            )}

                        {/* List */}
                        {articles && articles.length > 0 && (
                            <ul
                                data-testid="blog-list"
                                className="grid gap-5 sm:gap-6"
                            >
                                {articles.map((a, idx) => (
                                    <ArticleCard
                                        key={a.id || a.slug || idx}
                                        article={a}
                                        delay={Math.min(idx, 4) * 0.04}
                                        reduce={reduce}
                                    />
                                ))}
                            </ul>
                        )}

                        <p className="mt-12 text-[12.5px] text-[var(--brand-muted)]">
                            Have a topic you want us to write about?{" "}
                            <Link
                                to="/support"
                                className="underline decoration-[var(--brand-teal-2)] underline-offset-2 hover:text-[var(--brand-ink)]"
                            >
                                Send us a note
                            </Link>
                            .
                        </p>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}

function ArticleCard({ article, delay = 0, reduce = false }) {
    const { slug, title, excerpt, date, isoDate, image } = article || {};
    const anim = reduce
        ? {}
        : {
              initial: { opacity: 0, y: 14 },
              whileInView: { opacity: 1, y: 0 },
              viewport: { once: true, amount: 0.2 },
              transition: {
                  duration: 0.6,
                  ease: [0.2, 0.8, 0.2, 1],
                  delay,
              },
          };

    return (
        <motion.li {...anim}>
            <Link
                to={`/blog/${slug}`}
                data-testid={`blog-card-${slug}`}
                className="group relative flex flex-col sm:flex-row items-stretch overflow-hidden rounded-[18px] bg-white border border-brand shadow-[var(--shadow-soft)] transition-[transform,box-shadow,border-color] duration-300 ease-out hover:-translate-y-[2px] hover:shadow-[0_18px_44px_-18px_rgba(14,27,42,0.28)] hover:border-[rgba(48,140,138,0.45)] ring-focus"
            >
                {image ? (
                    <div className="relative sm:w-[260px] sm:shrink-0 aspect-[16/9] sm:aspect-auto sm:h-auto bg-[var(--brand-paper-2)] overflow-hidden">
                        <img
                            src={image}
                            alt=""
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                        />
                    </div>
                ) : null}

                <div className="flex-1 p-5 sm:p-7 flex flex-col">
                    {date ? (
                        <time
                            dateTime={isoDate || date}
                            className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[var(--brand-muted)]"
                        >
                            {date}
                        </time>
                    ) : null}
                    <h2 className="mt-2 font-serif text-[22px] sm:text-[24px] leading-[1.2] tracking-[-0.015em] text-[var(--brand-ink)] group-hover:text-[var(--brand-navy)] transition-colors">
                        {title || "Untitled note"}
                    </h2>
                    {excerpt ? (
                        <p className="mt-2.5 text-[14.5px] leading-[1.6] text-[var(--brand-muted-2)] line-clamp-3">
                            {excerpt}
                        </p>
                    ) : null}
                    <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-semibold tracking-[-0.01em] text-[var(--brand-teal-2)] group-hover:text-[var(--brand-navy)] transition-colors">
                        Read note
                        <ArrowRight
                            size={14}
                            strokeWidth={2}
                            className="transition-transform duration-300 group-hover:translate-x-0.5"
                        />
                    </span>
                </div>
            </Link>
        </motion.li>
    );
}
